import React, { useContext, useMemo } from "react";
import {
  Control,
  UseFormTrigger,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import {
  CheckboxController,
  Grid,
  GroupedController,
  NumberController,
  ReactSelectController,
  Text,
} from "@pushpress/shared-components";
import { getCurrencySymbol } from "../../../utils/currencyUtils";
import { AppContext } from "../../../context/app";
import { PayRatesValues } from "./AppointmentPayRateList.types";
import styles from "./PayRatesForm.module.scss";

interface PayRatesFormProps {
  control: Control<PayRatesValues>;
  trigger: UseFormTrigger<PayRatesValues>;
  onSubmit: VoidFunction;
}

function PayRatesForm({ control, trigger, onSubmit }: PayRatesFormProps) {
  const {
    client: { currencyIso },
  } = useContext(AppContext);

  const { fields } = useFieldArray({
    control,
    name: "types",
  });

  const values = useWatch({
    control,
  });

  const valueTypeOptions = useMemo(
    () => [
      {
        label: "%",
        value: "PERCENTAGE",
      },
      {
        label: getCurrencySymbol(currencyIso),
        value: "FLAT",
      },
    ],
    [currencyIso],
  );

  const valueChange = (index: number) => {
    trigger(`types.${index}`);
  };

  return (
    <form onSubmit={onSubmit} aria-label="pay rate configuration form">
      {fields.map((field, index) => {
        const isPercentage =
          values.types[index]?.rateType?.value === "PERCENTAGE";
        return (
          <Grid key={field.id} container>
            <Grid item xs={12}>
              <CheckboxController
                label={field.appointmentType.name}
                control={control}
                name={`types.${index}.enabled`}
              />
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={11}>
              {values!.types?.[index].enabled ? (
                <>
                  <GroupedController label="" flexItems={["0 0 86px", "1"]}>
                    <ReactSelectController
                      label="Option"
                      control={control}
                      name={`types.${index}.rateType`}
                      options={valueTypeOptions}
                      required
                      menuPortalTarget={document.body}
                      onChange={() => valueChange(index)}
                    />
                    <NumberController
                      label="Value"
                      control={control}
                      name={`types.${index}.value`}
                      required
                      min={1}
                      max={isPercentage ? 100 : undefined}
                      maxDecimalDigits={isPercentage ? 0 : 2}
                      onChange={() => valueChange(index)}
                    />
                  </GroupedController>
                </>
              ) : (
                <Text
                  variant="body-lg"
                  color="wolf-500"
                  className={styles.noRateText}
                >
                  No rate
                </Text>
              )}
            </Grid>
          </Grid>
        );
      })}
    </form>
  );
}

export default PayRatesForm;
